
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Zap, Plus, Trash2, Edit, Save, X, Search, 
  Sparkles, Loader2, Database, Download, Upload,
  Wand2, Brain, CheckCircle, AlertTriangle, 
  Layers, Target, Info, ShieldCheck, Check,
  ChevronRight, ArrowRight, ChevronLeft, Filter,
  ArrowUpDown, ArrowUp, ArrowDown, Square, CheckSquare,
  ListOrdered, Tag
} from 'lucide-react';
import { Flashcard, CertificationType, QuestionDifficulty } from '../../types';
import { StorageService } from '../../services/storageService';
import { generateFlashcardBatch } from '../../services/geminiService';
import { CNA_DOMAINS } from '../../data/mockData';

// Fallback domain maps strictly separated by certification type
const DEFAULT_CERT_DOMAINS: Record<string, string[]> = {
    cna: CNA_DOMAINS,
    phlebotomy: ['Venipuncture', 'Specimen Handling', 'Safety & Compliance', 'Order of Draw', 'Infection Control', 'Patient Identification'],
    medical_assistant: ['Clinical Procedures', 'Pharmacology', 'Administrative Protocols', 'Vital Signs & Patient Care', 'Laboratory Procedures', 'Medical Terminology'],
    pharmacy_tech: ['Dosage Calculations', 'Medication Safety', 'Sterile Compounding', 'Federal Regulations', 'Pharmacology Basics', 'Order Entry & Processing'],
    pct: ['Advanced Patient Care', 'EKG Monitoring', 'Phlebotomy Fundamentals', 'Infection Control', 'Hemodynamics', 'Activities of Daily Living']
};

export const FlashcardManager: React.FC = () => {
    const [certId, setCertId] = useState('cna');
    const [certs, setCerts] = useState<CertificationType[]>([]);
    
    // Grid State
    const [flashcards, setFlashcards] = useState<Flashcard[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(25);
    const [isLoading, setIsLoading] = useState(true);
    
    // Filter State
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [selectedDifficulty, setSelectedDifficulty] = useState('All');
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    // Selection
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    
    // AI / Modal States
    const [stagedCards, setStagedCards] = useState<Partial<Flashcard>[]>([]);
    const [selectedCard, setSelectedCard] = useState<Partial<Flashcard> | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);
    
    // AI Parameters
    const [aiTopic, setAiTopic] = useState('');
    const [isCustomTopic, setIsCustomTopic] = useState(false);
    const [aiCount, setAiCount] = useState(10);
    const [dbCategories, setDbCategories] = useState<string[]>([]);

    const [status, setStatus] = useState<{type: 'error' | 'success' | 'warning', text: string} | null>(null);

    useEffect(() => {
        let isMounted = true;
        StorageService.getFlashcardCategories(certId).then(res => {
            if (isMounted && res?.categories) {
                setDbCategories(res.categories);
            }
        }).catch(() => {});
        return () => { isMounted = false; };
    }, [certId]);

    useEffect(() => {
        const fetchCerts = () => {
            StorageService.getCertifications(true).then(data => {
                if (data && data.length > 0) {
                    setCerts(data);
                    if (certId !== 'cna' && !data.find(c => c.id === certId)) {
                        setCertId(data[0].id);
                    }
                }
            }).catch(err => {
                console.warn("Could not refresh certs in FlashcardManager:", err);
            });
        };

        fetchCerts();

        const handleCertsUpdated = () => {
            fetchCerts();
        };
        window.addEventListener('certifications-updated', handleCertsUpdated);
        return () => window.removeEventListener('certifications-updated', handleCertsUpdated);
    }, []);

    const loadData = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await StorageService.getFlashcards(certId, {
                page,
                limit,
                search: searchTerm.trim() || undefined,
                category: selectedCategory === 'All' ? undefined : selectedCategory,
                difficulty: selectedDifficulty === 'All' ? undefined : selectedDifficulty,
                sortBy,
                sortOrder
            });
            setFlashcards(data.items || []);
            setTotal(data.total || 0);
        } catch (e) {
            setStatus({ type: 'error', text: 'Registry Access Protocol Failure' });
        } finally {
            setIsLoading(false);
        }
    }, [certId, page, limit, searchTerm, selectedCategory, selectedDifficulty, sortBy, sortOrder]);

    useEffect(() => {
        const timer = setTimeout(loadData, 400); // Debounced refresh
        return () => clearTimeout(timer);
    }, [loadData]);

    const activeCert = certs.find(c => c.id === certId);

    // Available categories strictly scoped to active certification
    const availableCategories = useMemo(() => {
        const certCats = (activeCert?.categories || []).map(c => c.trim()).filter(Boolean);
        const catMap = new Map<string, string>();

        // 1. Categories configured in Cert Manager (authoritative source of truth)
        certCats.forEach(cat => {
            const key = cat.toLowerCase();
            if (!catMap.has(key)) catMap.set(key, cat);
        });

        // 2. Categories found across all stored flashcards in database for this certification
        dbCategories.forEach(cat => {
            if (cat && cat.trim()) {
                const trimmed = cat.trim();
                const key = trimmed.toLowerCase();
                if (!catMap.has(key)) catMap.set(key, trimmed);
            }
        });

        // 3. Categories found in current page of flashcards for this certification
        flashcards.forEach(fc => {
            if (fc.category && fc.category.trim()) {
                const trimmed = fc.category.trim();
                const key = trimmed.toLowerCase();
                if (!catMap.has(key)) catMap.set(key, trimmed);
            }
        });

        // 4. Specific fallback ONLY if no categories exist at all for this cert
        if (catMap.size === 0) {
            const defaultCats = DEFAULT_CERT_DOMAINS[certId.toLowerCase()] || [];
            defaultCats.forEach(cat => {
                const key = cat.toLowerCase();
                if (!catMap.has(key)) catMap.set(key, cat);
            });
        }

        return Array.from(catMap.values()).sort((a, b) => a.localeCompare(b));
    }, [activeCert, certId, dbCategories, flashcards]);

    // Keep aiTopic synchronized with available categories when certification changes
    useEffect(() => {
        if (availableCategories.length > 0) {
            if (!isCustomTopic || !aiTopic.trim()) {
                setAiTopic(availableCategories[0]);
                setIsCustomTopic(false);
            }
        } else {
            setAiTopic('');
        }
    }, [certId, availableCategories]);

    // Reset category filter and page when certification changes
    useEffect(() => {
        setSelectedCategory('All');
        setPage(1);
    }, [certId]);

    // Reset category filter if selected category is no longer valid for this cert
    useEffect(() => {
        if (selectedCategory !== 'All' && availableCategories.length > 0 && !availableCategories.includes(selectedCategory)) {
            setSelectedCategory('All');
        }
    }, [availableCategories, selectedCategory]);

    const handleSave = async () => {
        if (!selectedCard?.front || !selectedCard?.back) return;
        setIsSaving(true);
        try {
            if (selectedCard._id) {
                await StorageService.updateFlashcard(certId, selectedCard._id, selectedCard);
            } else {
                await StorageService.saveFlashcardsBulk(certId, [selectedCard]);
            }
            setStatus({ type: 'success', text: 'Flashcard synchronized with registry.' });
            setSelectedCard(null);
            loadData();
        } catch (e) {
            setStatus({ type: 'error', text: 'Commit protocol failure.' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Permanently purge this logic node?')) return;
        try {
            await StorageService.deleteFlashcard(certId, id);
            setStatus({ type: 'success', text: 'Node purged from registry.' });
            loadData();
        } catch (e) {
            setStatus({ type: 'error', text: 'Purge failed.' });
        }
    };

    const removeStaged = (index: number) => {
        setStagedCards(prev => prev.filter((_, i) => i !== index));
    };

    const handleGenerateAI = async () => {
        const topicToUse = aiTopic.trim();
        if (!topicToUse) return;
        setIsGenerating(true);
        setStatus(null);
        try {
            const certName = activeCert?.name || activeCert?.shortName || certId.toUpperCase();
            const batch = await generateFlashcardBatch(topicToUse, certName, aiCount, activeCert);
            // Ensure each synthesized item carries the selected category
            const normalized = batch.map(item => ({
                ...item,
                category: item.category?.trim() || topicToUse
            }));
            setStagedCards(prev => [...normalized, ...prev]);
            setStatus({ type: 'success', text: `Synthesized ${normalized.length} items for "${topicToUse}".` });
        } catch (e) {
            setStatus({ type: 'error', text: 'AI Synthesis engine failure.' });
        } finally {
            setIsGenerating(false);
        }
    };

    const handlePublishStaged = async () => {
        if (stagedCards.length === 0) return;
        setIsPublishing(true);
        setStatus(null);
        try {
            await StorageService.saveFlashcardsBulk(certId, stagedCards);
            setStatus({ type: 'success', text: `Published ${stagedCards.length} items to ${certId.toUpperCase()} registry.` });
            setStagedCards([]);
            loadData();
        } catch (e) {
            setStatus({ type: 'error', text: 'Cloud Commit Interrupted.' });
        } finally {
            setIsPublishing(false);
        }
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === flashcards.length) setSelectedIds(new Set());
        else setSelectedIds(new Set(flashcards.map(f => f._id)));
    };

    const toggleSelect = (id: string) => {
        const next = new Set(selectedIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelectedIds(next);
    };

    const handleBulkDelete = async () => {
        if (selectedIds.size === 0) return;
        if (!confirm(`Executing Bulk Purge on ${selectedIds.size} nodes. Proceed?`)) return;
        
        setIsBulkDeleting(true);
        try {
            await StorageService.deleteFlashcardsBulk(certId, Array.from(selectedIds));
            setSelectedIds(new Set());
            loadData();
            setStatus({ type: 'success', text: 'Bulk registry modification successful.' });
        } catch (e) {
            setStatus({ type: 'error', text: 'Purge protocol failed.' });
        } finally {
            setIsBulkDeleting(false);
        }
    };

    const handleSort = (key: string) => {
        if (sortBy === key) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(key);
            setSortOrder('desc');
        }
        setPage(1);
    };

    const totalPages = Math.ceil(total / limit);

    return (
        <div className="space-y-8 animate-fade-in pb-24 h-full flex flex-col text-slate-900 max-w-[1600px] mx-auto px-4 md:px-0">
            
            <div className="flex flex-col lg:flex-row justify-between items-stretch gap-6">
                <div className="flex-1 bg-slate-900 p-8 rounded-[2.5rem] shadow-2xl border border-white/5 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:rotate-12 transition-transform duration-1000 pointer-events-none"><Zap size={160}/></div>
                    <div className="relative z-10 flex-1">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-3 bg-indigo-600 rounded-2xl shadow-xl border-b-4 border-indigo-900">
                                <Sparkles size={24} className="text-white" />
                            </div>
                            <div>
                                <h2 className="text-3xl font-black text-white tracking-tight">Flashcard Studio</h2>
                                <p className="text-[10px] font-black text-indigo-400 tracking-[0.3em]">Institutional Registry Admin</p>
                            </div>
                        </div>
                        <p className="text-slate-400 font-medium text-sm md:text-lg max-w-2xl leading-relaxed">
                            Maintain the clinical recall database. Generate standard items via AI or provision manual board logic.
                        </p>
                        
                        <div className="mt-10 flex flex-wrap gap-3">
                            <div className="bg-white/5 p-1 rounded-2xl border border-white/10 flex items-center shrink-0">
                                <Database className="ml-3 text-indigo-400" size={16} />
                                <select 
                                    value={certId} 
                                    onChange={e => { setCertId(e.target.value); setSelectedCategory('All'); setPage(1); }}
                                    className="bg-transparent text-white px-4 py-2 rounded-xl outline-none font-black text-[10px] cursor-pointer"
                                >
                                    {certs.map(c => <option key={c.id} value={c.id} className="text-black">{c.shortName}</option>)}
                                </select>
                            </div>
                            <button 
                                onClick={() => setSelectedCard({ front: '', back: '', category: availableCategories[0] || 'General', difficulty: 'Medium' })}
                                className="px-8 py-4 bg-indigo-600 text-white rounded-2xl font-black text-[10px] tracking-widest flex items-center gap-2 hover:bg-indigo-50 shadow-xl border-b-4 border-indigo-900 active:scale-95"
                            >
                                <Plus size={18} /> Provision Asset
                            </button>
                        </div>
                    </div>
                </div>

                <div className="w-full lg:w-96 bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm space-y-6">
                    <h3 className="text-sm font-black tracking-widest text-slate-900 flex items-center gap-3"><Wand2 className="text-indigo-600"/> Quick Synthesis</h3>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-1.5">
                                    <Tag size={12} className="text-indigo-500" /> Clinical Category / Topic
                                </label>
                                {isCustomTopic ? (
                                    <button 
                                        type="button" 
                                        onClick={() => {
                                            setIsCustomTopic(false);
                                            if (availableCategories.length > 0) setAiTopic(availableCategories[0]);
                                        }}
                                        className="text-[9px] font-black text-indigo-600 hover:text-indigo-800 transition-colors"
                                    >
                                        Select Category
                                    </button>
                                ) : (
                                    <button 
                                        type="button" 
                                        onClick={() => {
                                            setIsCustomTopic(true);
                                            setAiTopic('');
                                        }}
                                        className="text-[9px] font-black text-slate-400 hover:text-indigo-600 transition-colors"
                                    >
                                        + Custom Topic
                                    </button>
                                )}
                            </div>

                            {!isCustomTopic && availableCategories.length > 0 ? (
                                <select 
                                    value={aiTopic} 
                                    onChange={e => {
                                        if (e.target.value === '__custom__') {
                                            setIsCustomTopic(true);
                                            setAiTopic('');
                                        } else {
                                            setAiTopic(e.target.value);
                                        }
                                    }}
                                    className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-xs focus:bg-white transition-all shadow-inner cursor-pointer"
                                >
                                    {availableCategories.map(cat => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                    <option value="__custom__">+ Enter Custom Topic...</option>
                                </select>
                            ) : (
                                <input 
                                    type="text" 
                                    value={aiTopic} 
                                    onChange={e => setAiTopic(e.target.value)}
                                    placeholder={availableCategories[0] ? `e.g., ${availableCategories[0]}...` : "e.g., Infection Control Protocols..."}
                                    className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-xs focus:bg-white transition-all shadow-inner"
                                />
                            )}
                        </div>
                        
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2">
                                <ListOrdered size={12}/> Synthesis Depth
                            </label>
                            <select 
                                value={aiCount}
                                onChange={e => setAiCount(parseInt(e.target.value))}
                                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-xs focus:bg-white transition-all shadow-inner cursor-pointer"
                            >
                                {[10, 20, 30, 40, 50].map(val => (
                                    <option key={val} value={val}>{val} Flashcards</option>
                                ))}
                            </select>
                        </div>

                        <button 
                            onClick={handleGenerateAI}
                            disabled={isGenerating || !aiTopic.trim()}
                            className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black text-[10px] tracking-widest shadow-xl flex items-center justify-center gap-3 active:scale-95 disabled:opacity-50 transition-all border-b-4 border-black"
                        >
                            {isGenerating ? <Loader2 size={16} className="animate-spin text-medical-400" /> : <Sparkles size={16} className="text-medical-400"/>}
                            Synthesize Batch
                        </button>
                    </div>
                </div>
            </div>

            {status && (
                <div className={`p-4 rounded-[1.5rem] flex items-center justify-between animate-pop border-2 shadow-xl ${
                    status.type === 'error' ? 'bg-rose-50 border-rose-100 text-rose-700' : 
                    status.type === 'warning' ? 'bg-amber-50 border-amber-100 text-amber-700' :
                    'bg-emerald-50 border-emerald-100 text-emerald-700'
                }`}>
                    <div className="flex items-center gap-3 ml-2">
                        {status.type === 'error' ? <AlertTriangle size={18}/> : status.type === 'warning' ? <Info size={18}/> : <CheckCircle size={18}/>}
                        <span className="font-black text-xs tracking-widest">{status.text}</span>
                    </div>
                    <button onClick={() => setStatus(null)} className="p-1 hover:bg-black/5 rounded-xl transition-all"><X size={16}/></button>
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 flex-1 min-h-0">
                
                {/* Main Registry Table */}
                <div className="xl:col-span-8 bg-white rounded-[3rem] border border-slate-200 shadow-sm overflow-hidden flex flex-col min-h-[600px]">
                    <div className="p-6 bg-slate-50 border-b border-slate-100 flex flex-col lg:flex-row items-center gap-4">
                        <div className="relative flex-1 w-full group">
                            <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                            <input 
                                type="text" 
                                placeholder={`Filter ${certId.toUpperCase()} registry logic...`} 
                                value={searchTerm}
                                onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
                                className="w-full pl-12 pr-4 py-3.5 bg-white border border-slate-200 rounded-2xl outline-none font-bold text-sm focus:ring-4 focus:ring-indigo-500/5 shadow-inner transition-all"
                            />
                        </div>
                        <div className="flex items-center gap-2 flex-wrap justify-center">
                            <div className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-xl">
                                <Filter size={14} className="text-slate-400" />
                                <select 
                                    value={selectedCategory} 
                                    onChange={e => { setSelectedCategory(e.target.value); setPage(1); }}
                                    className="bg-transparent border-none outline-none font-black text-[9px] text-slate-600 min-w-[100px]"
                                >
                                    <option value="All">All Domains</option>
                                    {availableCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                </select>
                            </div>
                            <div className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-xl">
                                <Zap size={14} className="text-slate-400" />
                                <select 
                                    value={selectedDifficulty} 
                                    onChange={e => { setSelectedDifficulty(e.target.value); setPage(1); }}
                                    className="bg-transparent border-none outline-none font-black text-[9px] text-slate-600 min-w-[80px]"
                                >
                                    <option value="All">All Rigor</option>
                                    <option value="Easy">Easy</option>
                                    <option value="Medium">Medium</option>
                                    <option value="Hard">Hard</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-x-auto custom-scrollbar relative">
                        <table className="w-full text-left border-collapse min-w-[800px]">
                            <thead className="bg-slate-50 sticky top-0 z-20">
                                <tr>
                                    <th className="py-4 px-6 w-12 text-center">
                                        <button onClick={toggleSelectAll} className={`p-1.5 rounded-lg transition-colors ${selectedIds.size === flashcards.length && flashcards.length > 0 ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-400'}`}>
                                            {selectedIds.size === flashcards.length && flashcards.length > 0 ? <CheckSquare size={14}/> : <Square size={14}/>}
                                        </button>
                                    </th>
                                    <th onClick={() => handleSort('front')} className="py-4 px-6 text-[9px] font-black text-slate-400 tracking-widest cursor-pointer group">
                                        <div className="flex items-center gap-2">
                                            Logic Stem {sortBy === 'front' ? (sortOrder === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ArrowUpDown size={10} className="opacity-0 group-hover:opacity-40" />}
                                        </div>
                                    </th>
                                    <th onClick={() => handleSort('category')} className="py-4 px-4 text-[9px] font-black text-slate-400 tracking-widest text-center cursor-pointer group">
                                        <div className="flex items-center justify-center gap-2">
                                            Domain {sortBy === 'category' ? (sortOrder === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ArrowUpDown size={10} className="opacity-0 group-hover:opacity-40" />}
                                        </div>
                                    </th>
                                    <th onClick={() => handleSort('difficulty')} className="py-4 px-4 text-[9px] font-black text-slate-400 tracking-widest text-center cursor-pointer group">
                                        <div className="flex items-center justify-center gap-2">
                                            Rigor {sortBy === 'difficulty' ? (sortOrder === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ArrowUpDown size={10} className="opacity-0 group-hover:opacity-40" />}
                                        </div>
                                    </th>
                                    <th className="py-4 px-8 text-right text-[9px] font-black text-slate-400 tracking-widest">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {isLoading ? (
                                    <tr><td colSpan={5} className="py-32 text-center opacity-30 animate-pulse"><Loader2 size={48} className="mx-auto mb-4 animate-spin"/> Hydrating Registry...</td></tr>
                                ) : flashcards.length === 0 ? (
                                    <tr><td colSpan={5} className="py-32 text-center opacity-40"><Database size={64} className="mx-auto mb-4"/> Namespace Empty</td></tr>
                                ) : flashcards.map(card => (
                                    <tr key={card._id} className={`group hover:bg-slate-50 transition-colors ${selectedIds.has(card._id) ? 'bg-indigo-50/30' : ''}`}>
                                        <td className="py-4 px-6 text-center">
                                            <button onClick={() => toggleSelect(card._id)} className={`p-1.5 rounded-lg transition-colors ${selectedIds.has(card._id) ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-300'}`}>
                                                {selectedIds.has(card._id) ? <CheckSquare size={14}/> : <Square size={14}/>}
                                            </button>
                                        </td>
                                        <td className="py-5 px-6">
                                            <div className="flex flex-col gap-1 min-w-0">
                                                <p className="font-black text-slate-800 tracking-tight text-[11px] truncate max-w-[400px]">"{card.front}"</p>
                                                <p className="text-[10px] text-slate-400 font-medium truncate max-w-[400px]">"{card.back}"</p>
                                            </div>
                                        </td>
                                        <td className="py-4 px-4 text-center">
                                            <span className="px-3 py-1 bg-slate-100 text-slate-500 rounded-lg text-[8px] font-black border border-slate-200">{card.category}</span>
                                        </td>
                                        <td className="py-4 px-4 text-center">
                                            <span className={`px-2.5 py-1 rounded-lg text-[8px] font-black ${
                                                card.difficulty === 'Hard' ? 'bg-rose-50 text-rose-600' :
                                                card.difficulty === 'Medium' ? 'bg-amber-50 text-amber-600' :
                                                'bg-emerald-50 text-emerald-600'
                                            }`}>
                                                {card.difficulty}
                                            </span>
                                        </td>
                                        <td className="py-4 px-8 text-right">
                                            <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-all">
                                                <button onClick={() => setSelectedCard(card)} className="p-2 text-slate-400 hover:text-indigo-600 bg-white border border-slate-100 rounded-xl shadow-sm"><Edit size={16}/></button>
                                                <button onClick={() => handleDelete(card._id)} className="p-2 text-slate-400 hover:text-rose-600 bg-white border border-slate-100 rounded-xl shadow-sm"><Trash2 size={16}/></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="px-8 py-5 border-t border-slate-50 flex items-center justify-between bg-slate-50/50 shrink-0">
                        <div className="flex items-center gap-6">
                            <div className="flex items-center gap-3">
                                <span className="text-[9px] font-black text-slate-400 tracking-widest">Rows</span>
                                <select 
                                    value={limit} 
                                    onChange={e => {setLimit(parseInt(e.target.value)); setPage(1);}}
                                    className="bg-white border border-slate-200 rounded-lg px-2 py-1 text-[10px] font-black outline-none focus:ring-2 focus:ring-indigo-500/20 shadow-sm"
                                >
                                    {[25, 50, 100].map(v => <option key={v} value={v}>{v}</option>)}
                                </select>
                            </div>
                            <p className="text-[9px] font-black text-slate-400 tracking-widest">Index: {((page - 1) * limit) + 1}-{Math.min(page * limit, total)} of {total}</p>
                        </div>
                        <div className="flex gap-2">
                            <button disabled={page === 1} onClick={() => setPage(page - 1)} className="p-2.5 rounded-xl bg-white border border-slate-200 text-slate-400 hover:text-slate-800 disabled:opacity-30 transition-all active:scale-90 shadow-sm"><ChevronLeft size={20}/></button>
                            <div className="px-5 py-2.5 bg-slate-900 text-white rounded-xl font-black text-[10px] shadow-lg shadow-slate-900/20">Phase {page} / {Math.max(1, totalPages)}</div>
                            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="p-2.5 rounded-xl bg-white border border-slate-200 text-slate-400 hover:text-slate-800 disabled:opacity-30 transition-all active:scale-90 shadow-sm"><ChevronRight size={20}/></button>
                        </div>
                    </div>
                </div>

                {/* RIGHT: PIPELINE / STAGING */}
                <div className="xl:col-span-4 flex flex-col gap-6">
                    {stagedCards.length > 0 && (
                        <div className="bg-slate-900 rounded-[3rem] p-8 text-white space-y-6 shadow-2xl relative overflow-hidden animate-slide-up border border-white/5">
                            <div className="absolute top-0 right-0 p-8 opacity-10 rotate-12"><Layers size={140}/></div>
                            <div className="relative z-10">
                                <div className="flex justify-between items-center mb-6">
                                    <div>
                                        <h3 className="text-xl font-black tracking-tight flex items-center gap-3"><Sparkles size={20} className="text-medical-400" /> Review Pipeline</h3>
                                        <p className="text-[10px] font-black text-indigo-400 tracking-widest mt-1">Staged Nodes: {stagedCards.length}</p>
                                    </div>
                                    <button onClick={() => setStagedCards([])} className="p-2 bg-white/5 hover:bg-rose-600/20 text-slate-500 hover:text-rose-400 rounded-xl transition-all"><X size={20}/></button>
                                </div>
                                
                                <div className="space-y-3 max-h-[500px] overflow-y-auto pr-3 custom-scrollbar">
                                    {stagedCards.map((card, idx) => (
                                        <div key={idx} className="p-5 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 transition-all relative group">
                                            <div className="flex justify-between items-start mb-3">
                                                <span className="px-2 py-0.5 bg-indigo-600 text-white rounded text-[7px] font-black">{card.category}</span>
                                                <button onClick={() => removeStaged(idx)} className="p-1 text-white/20 hover:text-rose-400"><Trash2 size={12}/></button>
                                            </div>
                                            <p className="font-bold text-xs leading-tight line-clamp-2">"{card.front}"</p>
                                        </div>
                                    ))}
                                </div>

                                <div className="pt-6 mt-6 border-t border-white/5">
                                    <button 
                                        onClick={handlePublishStaged}
                                        disabled={isPublishing}
                                        className="w-full py-5 bg-emerald-600 text-white rounded-[1.5rem] font-black text-xs tracking-widest shadow-2xl shadow-emerald-900/20 hover:bg-emerald-50 transition-all active:scale-95 border-b-4 border-emerald-800 flex items-center justify-center gap-3 disabled:opacity-50"
                                    >
                                        {isPublishing ? <Loader2 size={20} className="animate-spin" /> : <ShieldCheck size={20} />}
                                        Publish Staged Deck
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {selectedIds.size > 0 && (
                        <div className="bg-rose-950 p-8 rounded-[2.5rem] border border-rose-900 shadow-2xl animate-pop text-white relative overflow-hidden">
                            <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:rotate-12 transition-transform duration-1000"><Trash2 size={100}/></div>
                            <div className="relative z-10 space-y-6">
                                <h4 className="text-xl font-black tracking-tight flex items-center gap-3"><AlertTriangle className="text-rose-400" /> Bulk Deletion</h4>
                                <p className="text-sm font-medium text-rose-200/80 leading-relaxed">
                                     "Executing this command will permanently purge <span className="font-black text-white">{selectedIds.size}</span> items from the {certId.toUpperCase()} registry."
                                </p>
                                <button 
                                    onClick={handleBulkDelete}
                                    disabled={isBulkDeleting}
                                    className="w-full py-5 bg-rose-600 hover:bg-rose-50 text-white rounded-2xl font-black text-xs tracking-widest transition-all active:scale-95 border-b-4 border-rose-800 flex items-center justify-center gap-3 shadow-xl"
                                >
                                    {isBulkDeleting ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18}/>} 
                                    Execute Purge Protocol
                                </button>
                                <button onClick={() => setSelectedIds(new Set())} className="w-full py-3 text-[10px] font-black tracking-widest text-rose-300 hover:text-white">Cancel Operation</button>
                            </div>
                        </div>
                    )}

                    <div className="bg-white rounded-[2.5rem] p-8 border border-slate-200 shadow-sm space-y-6">
                        <h4 className="text-[10px] font-black text-slate-400 tracking-widest flex items-center gap-3 border-b border-slate-50 pb-4">
                            <Info size={16} className="text-indigo-500" /> Operational Context
                        </h4>
                        <div className="space-y-4">
                            <div className="flex gap-4">
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0"></div>
                                <p className="text-[10px] font-bold text-slate-500 leading-relaxed">Changes propagate to student study dashboards within 60 seconds.</p>
                            </div>
                            <div className="flex gap-4">
                                <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-1.5 shrink-0"></div>
                                <p className="text-[10px] font-bold text-slate-500 leading-relaxed">Flashcards Mastered by students are indexed at SRS Level 5.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* EDIT / PROVISION MODAL */}
            {selectedCard && (
                <div className="fixed inset-0 z-[300] bg-slate-950/90 backdrop-blur-xl flex items-start justify-center p-4 pt-10 md:pt-16 overflow-y-auto custom-scrollbar">
                    <div className="bg-white rounded-[3rem] shadow-2xl w-full max-w-4xl h-auto max-h-[90vh] flex flex-col animate-pop border border-white/10 overflow-hidden my-auto">
                        <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 shrink-0">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl shadow-inner"><Edit size={20}/></div>
                                <div>
                                    <h3 className="text-xl font-black text-slate-900 tracking-tight">Logic Node Config</h3>
                                    <p className="text-[10px] font-black text-slate-400 tracking-widest mt-1">{selectedCard._id ? 'Update Registry' : 'New Manual Provision'}</p>
                                </div>
                            </div>
                            <button onClick={() => setSelectedCard(null)} className="p-3 bg-white text-slate-400 rounded-2xl hover:text-rose-500 shadow-sm active:scale-90 transition-all"><X size={24}/></button>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto p-10 custom-scrollbar space-y-10">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                                <div className="space-y-8">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Logic Stem (Front)</label>
                                        <textarea 
                                            value={selectedCard.front} 
                                            onChange={e => setSelectedCard({...selectedCard, front: e.target.value})}
                                            placeholder="The patient presents with..."
                                            className="w-full p-6 bg-slate-50 border border-slate-100 rounded-[2rem] font-black text-xl md:text-2xl tracking-tighter outline-none focus:bg-white h-48 md:h-56 shadow-inner resize-none transition-all"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Active Domain</label>
                                            <select 
                                                value={selectedCard.category} 
                                                onChange={e => setSelectedCard({...selectedCard, category: e.target.value})}
                                                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-xs outline-none focus:bg-white transition-all shadow-sm"
                                            >
                                                {availableCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                                {!availableCategories.includes(selectedCard.category!) && selectedCard.category && <option value={selectedCard.category}>{selectedCard.category}</option>}
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Rigor Level</label>
                                            <select 
                                                value={selectedCard.difficulty} 
                                                onChange={e => setSelectedCard({...selectedCard, difficulty: e.target.value as any})}
                                                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl font-black text-[10px] outline-none focus:bg-white transition-all shadow-sm"
                                            >
                                                <option value="Easy">Easy</option>
                                                <option value="Medium">Medium</option>
                                                <option value="Hard">Hard</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-8">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Response Logic (Back)</label>
                                        <textarea 
                                            value={selectedCard.back} 
                                            onChange={e => setSelectedCard({...selectedCard, back: e.target.value})}
                                            placeholder="The definitive clinical action..."
                                            className="w-full p-6 bg-slate-50 border border-slate-100 rounded-[2rem] font-medium text-sm md:text-lg italic text-slate-700 outline-none focus:bg-white h-48 md:h-56 shadow-inner resize-none transition-all leading-relaxed"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2"><Sparkles size={12} className="text-indigo-500" /> Mnemonic Anchor</label>
                                        <input 
                                            type="text" value={selectedCard.mnemonic || ''} 
                                            onChange={e => setSelectedCard({...selectedCard, mnemonic: e.target.value})}
                                            className="w-full p-4 bg-indigo-50/30 border border-indigo-100 rounded-2xl font-medium text-xs md:text-sm text-indigo-900 outline-none focus:bg-white transition-all shadow-inner"
                                            placeholder="Recall trigger phrase..."
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-8 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-4 shrink-0">
                            <button onClick={() => setSelectedCard(null)} className="px-10 py-4 font-black text-[10px] text-slate-400 hover:text-slate-900 transition-colors">Discard</button>
                            <button 
                                onClick={handleSave} 
                                disabled={isSaving || !selectedCard.front || !selectedCard.back}
                                className="px-16 py-4 bg-slate-950 text-white rounded-2xl font-black text-[10px] tracking-widest shadow-2xl hover:bg-black transition-all flex items-center justify-center gap-3 disabled:opacity-50 border-b-4 border-black active:scale-95"
                            >
                                {isSaving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} 
                                Sync Logic Node
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const SRSMetric = ({ label, count, color, desc }: { label: string, count: number, color: string, desc: string }) => (
    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100 group transition-all hover:bg-white hover:border-indigo-100 hover:shadow-md">
        <div className="flex items-center gap-3">
            <div className={`w-2.5 h-2.5 rounded-full ${color} shadow-sm group-hover:scale-125 transition-transform`}></div>
            <div>
                <p className="text-[10px] font-black text-slate-800 tracking-tight leading-none">{label}</p>
                <p className="text-[7px] text-slate-400 mt-1 tracking-wider">{desc}</p>
            </div>
        </div>
        <span className="text-base font-black text-slate-900 tabular-nums">{count}</span>
    </div>
);
